const MARKER = 'sent TelemetryCallReport'
const MAX_USER_AGENT = 200

export type WebRtcCallReport = {
  /** callEndTime when it is a real unix time, otherwise when the log was received. */
  atMs: number
  iceConnectionsLost?: number
  iceConnectionsFailed?: number
  peerConnectionFailed?: boolean
  consecutiveNoAudioOutputPackets?: number
  consecutiveNoAudioInputPackets?: number
  consecutiveLowOutputAudioLevel?: number
  consecutiveLowInputAudioLevel?: number
  consecutiveAudioOutputMuteDurationS?: number
  talkingTimeMs?: number
  firstRtpTimeMs?: number
  iceCollectionFailure?: boolean
  signallingConnectionFailure?: boolean
  handshakingFailure?: boolean
  isMediaClusterPath?: boolean
  userAgent?: string
}

/**
 * End-of-call summary Streams logs as `sent TelemetryCallReport {json}`.
 * Missing keys stay absent. A numeric 0 is a real measurement and is kept.
 */
export function parseTelemetryCallReportLog(
  text: string,
  receivedAtMs: number,
): WebRtcCallReport | null {
  if (!text || !Number.isFinite(receivedAtMs)) return null
  const markerAt = text.indexOf(MARKER)
  if (markerAt < 0) return null
  const jsonAt = text.indexOf('{', markerAt + MARKER.length)
  if (jsonAt < 0) return null
  const raw = parseJsonObject(text.slice(jsonAt))
  if (!raw) return null

  const report: WebRtcCallReport = {
    atMs: callEndMs(raw.callEndTime, receivedAtMs),
  }
  assignInt(report, 'iceConnectionsLost', raw.iceConnectionsLost)
  assignInt(report, 'iceConnectionsFailed', raw.iceConnectionsFailed)
  assignBool(report, 'peerConnectionFailed', raw.peerConnectionFailed)
  assignInt(report, 'consecutiveNoAudioOutputPackets', raw.consecutiveNoAudioOutputPackets)
  assignInt(report, 'consecutiveNoAudioInputPackets', raw.consecutiveNoAudioInputPackets)
  assignInt(report, 'consecutiveLowOutputAudioLevel', raw.consecutiveLowOutputAudioLevel)
  assignInt(report, 'consecutiveLowInputAudioLevel', raw.consecutiveLowInputAudioLevel)
  assignInt(
    report,
    'consecutiveAudioOutputMuteDurationS',
    raw.consecutiveAudioOutputMuteDurationSeconds ?? raw.consecutiveAudioOutputMuteDurationS,
  )
  assignInt(report, 'talkingTimeMs', raw.talkingTimeMillis ?? raw.talkingTimeMs)
  assignInt(report, 'firstRtpTimeMs', raw.firstRtpTimeMillis ?? raw.firstRtpTimeMs)
  assignBool(report, 'iceCollectionFailure', raw.iceCollectionFailure)
  assignBool(report, 'signallingConnectionFailure', raw.signallingConnectionFailure)
  assignBool(report, 'handshakingFailure', raw.handshakingFailure)
  assignBool(report, 'isMediaClusterPath', raw.isMediaClusterPath)
  const userAgent = truncate(raw.userAgent ?? raw.user_agent, MAX_USER_AGENT)
  if (userAgent) report.userAgent = userAgent
  return report
}

function callEndMs(value: unknown, receivedAtMs: number): number {
  if (typeof value === 'string' && value.trim()) {
    const parsed = Date.parse(value)
    if (Number.isFinite(parsed) && parsed >= 1_000_000_000_000) return parsed
  }
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n <= 0) return receivedAtMs
  if (n >= 1_000_000_000_000) return Math.round(n)
  if (n >= 1_000_000_000) return Math.round(n * 1000)
  return receivedAtMs
}

function assignInt<K extends keyof WebRtcCallReport>(
  report: WebRtcCallReport,
  key: K,
  value: unknown,
): void {
  if (value == null || value === '') return
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n < 0) return
  ;(report[key] as unknown) = Math.round(n)
}

function assignBool<K extends keyof WebRtcCallReport>(
  report: WebRtcCallReport,
  key: K,
  value: unknown,
): void {
  if (typeof value !== 'boolean') return
  ;(report[key] as unknown) = value
}

function truncate(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  return trimmed.length <= max ? trimmed : trimmed.slice(0, max)
}

function parseJsonObject(text: string): Record<string, unknown> | null {
  let depth = 0
  let inString = false
  let escape = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inString) {
      if (escape) escape = false
      else if (ch === '\\') escape = true
      else if (ch === '"') inString = false
      continue
    }
    if (ch === '"') {
      inString = true
      continue
    }
    if (ch === '{') depth++
    else if (ch === '}') {
      depth--
      if (depth === 0) {
        try {
          const parsed = JSON.parse(text.slice(0, i + 1)) as unknown
          return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Record<string, unknown>)
            : null
        } catch {
          return null
        }
      }
    }
  }
  return null
}
